import type { Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import type { IdentifierFactory } from '@toolproof-core/lib/_contract/identifiers';
import {
    bindExternalInputPotential,
    bindInternalInputPotential,
    bindMaterializedResource2,
    materializeIntrinsicToolStepInputs,
    setWorkflowStateEntry,
} from '@toolproof-core/lib/_pure/mutable-workflow-overlay';
import { createResourceFromDescriptor } from '@toolproof-core/lib/_pure/resource-creation';
import {
    createLoopSlotWhenToolStepFromTool,
    createToolStepFromTool,
} from '@toolproof-core/lib/_pure/step-creation';
import {
    deriveLoopFeedbackBindingSpec,
    isLoopFeedbackBindingSpecCompatible,
} from '@toolproof-core/lib/_pure/tool-filters';
import { getBindableInputRoleNames } from '@toolproof-core/lib/_pure/workflow-extraction';
import type {
    MutableWorkflowStateEntry,
} from '@toolproof-core/lib/_contract/workflow-overlay';
import type {
    MutableToolStepKey,
    MutableWorkflow,
    MutableWorkflowState,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import { createActionError } from '@/workflow-builder/_pure/model/error';
import { resetExecution } from '@/workflow-builder/_pure/model/executionState';
import {
    createCaseDraftPlaceholderIndices,
    createEmptyDraft,
    deriveActiveTargetAddress,
    deriveIdentityToolHandleFromResourceType,
    deriveInitialCursor,
    deriveWhenInputRoleBindingSpec,
    writeToolStepToCursorInDraft,
} from '@/workflow-builder/_pure/model/machineDraft';
import {
    buildToolStepByKey,
    collectDraftToolSteps,
    collectMutableWorkflowToolSteps,
    deriveCanonicalBranchOutputTypeHandle,
    mergeDraftIntoMutableWorkflow,
    mergeWorkflowStateEntries,
} from '@/workflow-builder/_pure/model/machineWorkflow';
import {
    deriveBindingProgressionAfterBind,
    deriveNextBindingPhaseFromSelection,
} from '@/workflow-builder/_pure/model/reducerBindingPhase';
import {
    deriveDiscardDraftTransition,
    deriveFinalizeCurrentToolBindingsTransition,
} from '@/workflow-builder/_pure/model/reducerCursorTransitions';
import {
    clearBranchCaseWhat,
    clearBranchCaseWhen,
    clearBranchDefaultWhat,
    clearBranchDefaultWhen,
    clearLoopCaseWhatAndWhen,
    clearLoopCaseWhen,
    isLoopCaseCleared,
} from '@/workflow-builder/_pure/model/reducerDraftUpdates';
import {
    hasDraftAndActiveSlotCursor,
    hasDraftAndBindingPhase,
    hasReviewScopeForFinalizeCurrentToolBindings,
    isBindingPhase,
    isBranchDraft,
    isLoopDraft,
} from '@/workflow-builder/_pure/model/reducerGuards';
import { withClearedSelection } from '@/workflow-builder/_pure/model/reducerStateShapes';
import {
    deriveFirstInputRoleName,
    deriveNextUnboundInputRoleName,
} from '@/workflow-builder/_pure/model/toolStepDraft';
import type { Action } from '@/workflow-builder/_contract/command';
import type { Draft } from '@/workflow-builder/_contract/draft';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

function createBooleanMaterializedResource(value: boolean, generateIdentifier: IdentifierFactory) {
    return {
        ...createResourceFromDescriptor(
            {
                id: generateIdentifier('ResourceId'),
                resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
            },
            value,
        ),
        workflowStateEntryKind: 'materializedResource' as const,
    };
}

function createNaturalMaterializedResource(value: number, generateIdentifier: IdentifierFactory) {
    return {
        ...createResourceFromDescriptor(
            {
                id: generateIdentifier('ResourceId'),
                resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            },
            value,
        ),
        workflowStateEntryKind: 'materializedResource' as const,
    };
}

function withError(state: RuntimeState, action: Action['command'], message: string): RuntimeState {
    return {
        ...state,
        error: createActionError(action, message),
    };
}

function getSelectedTool(state: RuntimeState): Tool | null {
    const selectedToolStep = state.selectionState.selectedToolStep;
    return selectedToolStep ? state.toolMap.get(selectedToolStep.toolHandle) ?? null : null;
}

function isBindableTarget(state: RuntimeState, roleName: string): boolean {
    const tool = getSelectedTool(state);
    return tool !== null && getBindableInputRoleNames(tool).includes(roleName);
}

function getSelectedBindableInputRoleNames(state: RuntimeState): string[] {
    const tool = getSelectedTool(state);
    return tool === null ? [] : getBindableInputRoleNames(tool);
}

function deriveNaturalBindingInputTextFromSelection(args: {
    workflowState: MutableWorkflowState;
    selectedToolStep: RuntimeState['selectionState']['selectedToolStep'];
    selectedInputRoleName: RuntimeState['selectionState']['selectedInputRoleName'];
}): string {
    if (!args.selectedToolStep || !args.selectedInputRoleName) {
        return '';
    }

    const selectedEntry: MutableWorkflowStateEntry | undefined = args.workflowState[args.selectedToolStep.stepKey]?.[args.selectedInputRoleName];
    if (!selectedEntry || selectedEntry.workflowStateEntryKind !== 'materializedResource') {
        return '';
    }

    if (selectedEntry.resourceTypeHandle !== CONSTANTS.Handles.ResourceTypes.TYPE_Natural) {
        return '';
    }

    if (typeof selectedEntry.projection !== 'number') {
        return '';
    }

    return String(selectedEntry.projection);
}

export function createInitialRuntimeState(args: {
    mutableWorkflow: MutableWorkflow;
    toolMap: Readonly<Map<ToolHandle, Tool>>;
}): RuntimeState {
    return {
        adapterMode: 'builder',
        phase: 'idle',
        draft: null,
        activeSlotCursor: null,
        mutableWorkflow: args.mutableWorkflow,
        toolMap: args.toolMap,
        naturalBindingInputText: '',
        executionState: {
            executionStateKind: 'idle',
            lastRequestId: 0,
        },
        selectionState: {
            selectedToolStep: null,
            selectedInputRoleName: null,
        },
        error: null,
    };
}

export function reduceAction(
    state: RuntimeState,
    action: Action,
    generateIdentifier: IdentifierFactory,
): RuntimeState {
    switch (action.command) {
        case 'create_draft': {
            if (state.draft !== null) {
                return withError(state, action.command, 'A draft is already active.');
            }

            if (
                action.stepKind !== 'tool'
                && action.stepKind !== 'branch'
                && action.stepKind !== 'while'
                && action.stepKind !== 'for'
            ) {
                return withError(state, action.command, `Unsupported stepKind '${action.stepKind}'.`);
            }

            const draft = createEmptyDraft(action.stepKind);

            const nextState = {
                ...resetExecution(state),
                phase: 'selecting_tool_at_slot' as const,
                draft,
                activeSlotCursor: deriveInitialCursor(action.stepKind),
                error: null,
            };

            if (action.stepKind !== 'branch') {
                return nextState;
            }

            return reduceAction(nextState, {
                command: 'create_tool_step',
                toolHandle: CONSTANTS.Handles.Tools.TOOL_BooleanIdentity,
            }, generateIdentifier);
        }

        case 'create_tool_step': {
            if (state.draft === null || state.phase !== 'selecting_tool_at_slot' || state.activeSlotCursor === null) {
                return withError(state, action.command, 'No active tool selection cursor.');
            }

            const tool = state.toolMap.get(action.toolHandle);
            if (!tool) {
                return withError(state, action.command, `Tool '${action.toolHandle}' is unavailable.`);
            }

            const toolStep = state.activeSlotCursor.cursorKind === 'case/when'
                ? (() => {
                    const whenSpec = deriveWhenInputRoleBindingSpec(tool);
                    return whenSpec
                        ? createLoopSlotWhenToolStepFromTool(tool, whenSpec, generateIdentifier)
                        : createToolStepFromTool(tool, generateIdentifier); // ATTENTION: trust invariants
                })()
                : createToolStepFromTool(tool, generateIdentifier);

            let nextDraft = writeToolStepToCursorInDraft({
                draft: state.draft,
                cursor: state.activeSlotCursor,
                toolStep,
            });

            if (state.activeSlotCursor.cursorKind === 'case/what' && isLoopDraft(nextDraft)) {
                const loopFeedbackBindingSpec = deriveLoopFeedbackBindingSpec(tool);
                if (loopFeedbackBindingSpec === null) {
                    return withError(state, action.command, `Tool '${tool.handle}' has no unique loop feedback binding.`);
                }
                nextDraft = {
                    ...nextDraft,
                    caseDraft: {
                        ...nextDraft.caseDraft,
                        loopFeedbackBindingSpec,
                    },
                };
            }

            try {
                nextDraft = {
                    ...nextDraft,
                    workflowState: materializeIntrinsicToolStepInputs({
                        workflowState: nextDraft.workflowState,
                        toolStep,
                        tool,
                        generateIdentifier,
                    }),
                };
            } catch (error) {
                return withError(state, action.command, error instanceof Error ? error.message : 'Failed to materialize intrinsic inputs.');
            }

            const bindableInputRoleNames = getBindableInputRoleNames(tool);
            const selectedInputRoleName = deriveFirstInputRoleName(bindableInputRoleNames);

            return {
                ...state,
                phase: selectedInputRoleName === null ? 'reviewing_input_bindings' : 'binding_inputs',
                draft: nextDraft,
                selectionState: {
                    selectedToolStep: toolStep,
                    selectedInputRoleName,
                },
                error: null,
            };
        }

        case 'select_input_to_bind': {
            if (!isBindingPhase(state.phase)) {
                return withError(state, action.command, 'Input selection is only allowed while binding/reviewing inputs.');
            }
            if (!isBindableTarget(state, action.roleName)) {
                return withError(state, action.command, `Input role '${action.roleName}' is intrinsic or unavailable.`);
            }

            const validationWorkflowState = mergeWorkflowStateEntries(
                state.mutableWorkflow.workflowState,
                state.draft?.workflowState ?? {},
            );

            return {
                ...state,
                naturalBindingInputText: deriveNaturalBindingInputTextFromSelection({
                    workflowState: validationWorkflowState,
                    selectedToolStep: state.selectionState.selectedToolStep,
                    selectedInputRoleName: action.roleName,
                }),
                selectionState: {
                    ...state.selectionState,
                    selectedInputRoleName: action.roleName,
                },
                error: null,
            };
        }

        case 'select_tool_step_to_bind': {
            if (
                state.phase !== 'idle'
                && state.phase !== 'reviewing_input_bindings'
            ) {
                return withError(state, action.command, 'ToolStep selection is only allowed while idle/reviewing inputs.');
            }

            const isIdleBootstrap = state.draft === null && state.phase === 'idle';
            const seededWorkingDraft = state.draft ?? (isIdleBootstrap ? createEmptyDraft('tool') : null);
            if (seededWorkingDraft === null) {
                return withError(state, action.command, 'No active draft binding scope.');
            }

            const workingActiveSlotCursor = state.activeSlotCursor ?? (state.phase === 'idle' ? { cursorKind: 'self' } : null);
            if (workingActiveSlotCursor === null) {
                return withError(state, action.command, 'No active draft cursor scope.');
            }

            const draftToolStepByKey = new Map(
                collectDraftToolSteps(seededWorkingDraft).map((toolStep) => [toolStep.stepKey, toolStep]),
            );
            const selectedToolStep = draftToolStepByKey.get(action.stepKey as MutableToolStepKey)
                ?? collectMutableWorkflowToolSteps(state.mutableWorkflow).find((toolStep) => toolStep.stepKey === action.stepKey)
                ?? null;

            if (!selectedToolStep) {
                return withError(state, action.command, `ToolStep '${action.stepKey}' is unavailable.`);
            }

            const hydratedWorkingDraft = isIdleBootstrap
                ? {
                    ...seededWorkingDraft,
                    workflowState: (() => {
                        const committedStepEntries = state.mutableWorkflow.workflowState[selectedToolStep.stepKey] ?? null;
                        if (!committedStepEntries) {
                            return seededWorkingDraft.workflowState;
                        }
                        return mergeWorkflowStateEntries(
                            seededWorkingDraft.workflowState,
                            { [selectedToolStep.stepKey]: committedStepEntries } as MutableWorkflowState,
                        );
                    })(),
                }
                : seededWorkingDraft;

            if (
                state.phase === 'reviewing_input_bindings'
                && (hydratedWorkingDraft.draftKind === 'branch' || hydratedWorkingDraft.draftKind === 'while' || hydratedWorkingDraft.draftKind === 'for')
            ) {
                const draftStepKeySet = new Set(collectDraftToolSteps(hydratedWorkingDraft).map((toolStep) => toolStep.stepKey));
                if (!draftStepKeySet.has(selectedToolStep.stepKey)) {
                    return withError(state, action.command, 'ToolStep selection is limited to the active MacroStep while reviewing inputs.');
                }
            }

            const validationWorkflowState = mergeWorkflowStateEntries(
                state.mutableWorkflow.workflowState,
                hydratedWorkingDraft.workflowState,
            );

            const nextPhase = deriveNextBindingPhaseFromSelection({
                currentPhase: state.phase,
                selectedToolStep,
                draftWorkflowState: validationWorkflowState,
            });

            const selectedTool = state.toolMap.get(selectedToolStep.toolHandle);
            if (!selectedTool) {
                return withError(state, action.command, `Tool '${selectedToolStep.toolHandle}' is unavailable.`);
            }
            const roleNames = getBindableInputRoleNames(selectedTool);
            const roleEntries = validationWorkflowState[selectedToolStep.stepKey] ?? {};
            const inputBindings: Record<string, true | null> = Object.fromEntries(
                roleNames.map((roleName) => [roleName, roleName in roleEntries ? true : null]),
            );
            const nextSelectedInputRoleName = deriveNextUnboundInputRoleName({
                roleNames,
                inputBindings,
                lastBoundRoleName: state.selectionState.selectedInputRoleName ?? undefined,
            });

            return {
                ...state,
                phase: nextPhase,
                draft: hydratedWorkingDraft,
                activeSlotCursor: workingActiveSlotCursor,
                naturalBindingInputText: deriveNaturalBindingInputTextFromSelection({
                    workflowState: validationWorkflowState,
                    selectedToolStep,
                    selectedInputRoleName: nextSelectedInputRoleName,
                }),
                selectionState: {
                    selectedToolStep,
                    selectedInputRoleName: nextSelectedInputRoleName,
                },
                error: null,
            };
        }

        case 'bind_materialized_resource': {
            if (!hasDraftAndBindingPhase(state)) {
                return withError(state, action.command, 'No active draft binding scope.');
            }

            const targetAddress = deriveActiveTargetAddress(state);
            if (!targetAddress) {
                return withError(state, action.command, 'No active binding target selected.');
            }
            if (!isBindableTarget(state, targetAddress.roleName)) {
                return withError(state, action.command, `Input role '${targetAddress.roleName}' is intrinsic or unavailable.`);
            }

            const nextDraft: Draft = {
                ...state.draft,
                workflowState: bindMaterializedResource2(
                    state.draft.workflowState,
                    targetAddress,
                    action.materializedResource,
                ),
            };
            const nextValidationWorkflowState = mergeWorkflowStateEntries(
                state.mutableWorkflow.workflowState,
                nextDraft.workflowState,
            );
            const {
                nextPhase,
                nextSelectedInputRoleName,
            } = deriveBindingProgressionAfterBind({
                currentPhase: state.phase,
                selectedToolStep: state.selectionState.selectedToolStep,
                selectedInputRoleName: state.selectionState.selectedInputRoleName,
                draftWorkflowState: nextValidationWorkflowState,
                bindableInputRoleNames: getSelectedBindableInputRoleNames(state),
            });

            return {
                ...state,
                phase: nextPhase,
                draft: nextDraft,
                naturalBindingInputText: deriveNaturalBindingInputTextFromSelection({
                    workflowState: nextValidationWorkflowState,
                    selectedToolStep: state.selectionState.selectedToolStep,
                    selectedInputRoleName: nextSelectedInputRoleName,
                }),
                selectionState: {
                    ...state.selectionState,
                    selectedInputRoleName: nextSelectedInputRoleName,
                },
                error: null,
            };
        }

        case 'bind_internal_input_potential': {
            if (!hasDraftAndBindingPhase(state)) {
                return withError(state, action.command, 'No active draft binding scope.');
            }

            const targetAddress = deriveActiveTargetAddress(state);
            if (!targetAddress) {
                return withError(state, action.command, 'No active binding target selected.');
            }
            if (!isBindableTarget(state, targetAddress.roleName)) {
                return withError(state, action.command, `Input role '${targetAddress.roleName}' is intrinsic or unavailable.`);
            }

            const toolStepByKey = buildToolStepByKey({
                mutableWorkflow: state.mutableWorkflow,
                draft: state.draft,
            });

            const validationWorkflowState = mergeWorkflowStateEntries(
                state.mutableWorkflow.workflowState,
                state.draft.workflowState,
            );

            let nextWorkflowState: MutableWorkflowState;
            try {
                nextWorkflowState = bindInternalInputPotential(
                    validationWorkflowState,
                    targetAddress,
                    action.sourceAddress,
                    {
                        toolStepByKey,
                        toolMap: state.toolMap,
                    },
                );
            } catch (error) {
                return withError(state, action.command, error instanceof Error ? error.message : 'Failed to bind internal input potential.');
            }

            const nextDraftEntry = nextWorkflowState[targetAddress.stepKey]?.[targetAddress.roleName];
            const nextDraft: Draft = {
                ...state.draft,
                workflowState: nextDraftEntry
                    ? setWorkflowStateEntry(state.draft.workflowState, targetAddress, nextDraftEntry)
                    : state.draft.workflowState,
            };
            const nextValidationWorkflowState = mergeWorkflowStateEntries(
                state.mutableWorkflow.workflowState,
                nextDraft.workflowState,
            );
            const {
                nextPhase,
                nextSelectedInputRoleName,
            } = deriveBindingProgressionAfterBind({
                currentPhase: state.phase,
                selectedToolStep: state.selectionState.selectedToolStep,
                selectedInputRoleName: state.selectionState.selectedInputRoleName,
                draftWorkflowState: nextValidationWorkflowState,
                bindableInputRoleNames: getSelectedBindableInputRoleNames(state),
            });

            return {
                ...state,
                phase: nextPhase,
                draft: nextDraft,
                naturalBindingInputText: deriveNaturalBindingInputTextFromSelection({
                    workflowState: nextValidationWorkflowState,
                    selectedToolStep: state.selectionState.selectedToolStep,
                    selectedInputRoleName: nextSelectedInputRoleName,
                }),
                selectionState: {
                    ...state.selectionState,
                    selectedInputRoleName: nextSelectedInputRoleName,
                },
                error: null,
            };
        }

        case 'bind_external_input_potential': {
            if (!hasDraftAndBindingPhase(state)) {
                return withError(state, action.command, 'No active draft binding scope.');
            }

            const targetAddress = deriveActiveTargetAddress(state);
            if (!targetAddress) {
                return withError(state, action.command, 'No active binding target selected.');
            }
            if (!isBindableTarget(state, targetAddress.roleName)) {
                return withError(state, action.command, `Input role '${targetAddress.roleName}' is intrinsic or unavailable.`);
            }

            const nextDraft: Draft = {
                ...state.draft,
                workflowState: bindExternalInputPotential(
                    state.draft.workflowState,
                    targetAddress,
                ),
            };
            const nextValidationWorkflowState = mergeWorkflowStateEntries(
                state.mutableWorkflow.workflowState,
                nextDraft.workflowState,
            );
            const nextPhase = deriveNextBindingPhaseFromSelection({
                currentPhase: state.phase,
                selectedToolStep: state.selectionState.selectedToolStep,
                draftWorkflowState: nextValidationWorkflowState,
            });

            return {
                ...state,
                phase: nextPhase,
                draft: nextDraft,
                naturalBindingInputText: deriveNaturalBindingInputTextFromSelection({
                    workflowState: nextValidationWorkflowState,
                    selectedToolStep: state.selectionState.selectedToolStep,
                    selectedInputRoleName: state.selectionState.selectedInputRoleName,
                }),
                error: null,
            };
        }

        case 'finalize_current_tool_bindings': {
            if (!hasReviewScopeForFinalizeCurrentToolBindings(state)) {
                return withError(state, action.command, 'No review scope is active.');
            }

            const transition = deriveFinalizeCurrentToolBindingsTransition(state.activeSlotCursor);

            switch (transition.kind) {
                case 'finalize_draft':
                    return reduceAction(state, { command: 'finalize_draft' }, generateIdentifier);
                case 'set_cursor': {
                    if (transition.requiresBranchDraft && !isBranchDraft(state.draft)) {
                        return withError(state, action.command, 'Default branch cursor requires a branch draft.');
                    }

                    const branchDraft = transition.requiresBranchDraft && state.draft && isBranchDraft(state.draft)
                        ? state.draft
                        : null;

                    if (
                        transition.requiresBranchDraft
                        && transition.activeSlotCursor.cursorKind === 'branch/default/what'
                        && branchDraft !== null
                    ) {
                        const canonicalTypeHandle = deriveCanonicalBranchOutputTypeHandle(branchDraft.caseSetDraft, state.toolMap);
                        if (!canonicalTypeHandle) {
                            return withError(state, action.command, 'Could not derive canonical branch output type.');
                        }
                        const identityToolHandle = deriveIdentityToolHandleFromResourceType(canonicalTypeHandle);
                        if (!identityToolHandle) {
                            return withError(state, action.command, `Could not derive identity tool for '${canonicalTypeHandle}'.`);
                        }

                        const nextState = withClearedSelection({
                            ...state,
                            phase: transition.phase,
                            activeSlotCursor: transition.activeSlotCursor,
                            error: null,
                        });

                        return reduceAction(nextState, {
                            command: 'create_tool_step',
                            toolHandle: identityToolHandle,
                        }, generateIdentifier);
                    }

                    const nextState = withClearedSelection({
                        ...state,
                        phase: transition.phase,
                        activeSlotCursor: transition.activeSlotCursor,
                        error: null,
                    });

                    const shouldAutoSelectLoopWhenTool =
                        transition.phase === 'selecting_tool_at_slot'
                        && transition.activeSlotCursor.cursorKind === 'case/when'
                        && nextState.draft !== null
                        && isLoopDraft(nextState.draft);

                    if (!shouldAutoSelectLoopWhenTool) {
                        return nextState;
                    }

                    const autoSelectedWhenState = reduceAction(nextState, {
                        command: 'create_tool_step',
                        toolHandle: CONSTANTS.Handles.Tools.TOOL_NaturalLessThan,
                    }, generateIdentifier);

                    const autoSelectedWhenDraft = autoSelectedWhenState.draft;

                    if (
                        autoSelectedWhenDraft === null
                        || !isLoopDraft(autoSelectedWhenDraft)
                        || autoSelectedWhenState.selectionState.selectedToolStep === null
                    ) {
                        return autoSelectedWhenState;
                    }

                    const dynamicRoleName = autoSelectedWhenState.selectionState.selectedToolStep
                        .whenInputRoleBindingSpec
                        ?.dynamicInputRoleNames[0]
                        ?? null;

                    if (dynamicRoleName === null) {
                        return autoSelectedWhenState;
                    }

                    if (!autoSelectedWhenState.selectionState.selectedToolStep.roleBindingSpec.inputBindings.includes(dynamicRoleName)) {
                        return autoSelectedWhenState;
                    }

                    const focusedDynamicRoleState =
                        autoSelectedWhenState.selectionState.selectedInputRoleName === dynamicRoleName
                            ? autoSelectedWhenState
                            : reduceAction(autoSelectedWhenState, {
                                command: 'select_input_to_bind',
                                roleName: dynamicRoleName,
                            }, generateIdentifier);

                    if (focusedDynamicRoleState.selectionState.selectedInputRoleName !== dynamicRoleName) {
                        return focusedDynamicRoleState;
                    }

                    const focusedDynamicRoleDraft = focusedDynamicRoleState.draft;
                    if (focusedDynamicRoleDraft === null || !isLoopDraft(focusedDynamicRoleDraft)) {
                        return focusedDynamicRoleState;
                    }

                    if (focusedDynamicRoleDraft.draftKind === 'while') {
                        const sourceWhatToolStep = focusedDynamicRoleDraft.caseDraft.what;
                        const sourceWhatRoleName = sourceWhatToolStep?.roleBindingSpec.outputBindings[0] ?? null;
                        if (!sourceWhatToolStep || sourceWhatRoleName === null) {
                            return focusedDynamicRoleState;
                        }

                        return reduceAction(focusedDynamicRoleState, {
                            command: 'bind_internal_input_potential',
                            sourceAddress: {
                                stepKey: sourceWhatToolStep.stepKey,
                                roleName: sourceWhatRoleName,
                            },
                        }, generateIdentifier);
                    }

                    return reduceAction(focusedDynamicRoleState, {
                        command: 'bind_materialized_resource',
                        materializedResource: createNaturalMaterializedResource(1, generateIdentifier),
                    }, generateIdentifier);
                }
                case 'error_branch_editing':
                    return withError(state, action.command, 'No active tool slot to finalize while branch editing.');
            }
        }

        case 'add_branch_case': {
            if (!state.draft || !isBranchDraft(state.draft) || state.phase !== 'reviewing_branch_cases') {
                return withError(state, action.command, 'Branch case editing is not active.');
            }

            const nextCaseIndex = state.draft.caseSetDraft.cases.length;
            const nextDraft: Draft = {
                ...state.draft,
                caseSetDraft: {
                    ...state.draft.caseSetDraft,
                    cases: [...state.draft.caseSetDraft.cases, {
                        caseKey: `case-${nextCaseIndex + 1}`,
                        when: null,
                        what: null,
                        loopFeedbackBindingSpec: null,
                        ...createCaseDraftPlaceholderIndices({
                            draftKind: 'branch',
                            caseIndex: nextCaseIndex,
                        }),
                    }],
                    activeCaseIndex: nextCaseIndex,
                },
            };

            const nextState: RuntimeState = {
                ...withClearedSelection(state),
                phase: 'selecting_tool_at_slot',
                draft: nextDraft,
                activeSlotCursor: { cursorKind: 'cases/when', caseIndex: nextCaseIndex },
                error: null,
            };

            return reduceAction(nextState, {
                command: 'create_tool_step',
                toolHandle: CONSTANTS.Handles.Tools.TOOL_BooleanIdentity,
            }, generateIdentifier);
        }

        case 'begin_branch_default_case': {
            if (!state.draft || !isBranchDraft(state.draft) || state.phase !== 'reviewing_branch_cases') {
                return withError(state, action.command, 'Branch case editing is not active.');
            }

            if (state.draft.caseSetDraft.defaultCase !== null) {
                return withError(state, action.command, 'Default branch case has already been started.');
            }

            const defaultCaseIndex = state.draft.caseSetDraft.cases.length;
            const seededState: RuntimeState = {
                ...withClearedSelection(state),
                phase: 'selecting_tool_at_slot',
                activeSlotCursor: {
                    cursorKind: 'branch/default/when',
                    caseIndex: defaultCaseIndex,
                } as const,
                draft: {
                    ...state.draft,
                    caseSetDraft: {
                        ...state.draft.caseSetDraft,
                        defaultCase: {
                            caseKey: `case-default-${defaultCaseIndex + 1}`,
                            when: null,
                            what: null,
                            loopFeedbackBindingSpec: null,
                            ...createCaseDraftPlaceholderIndices({
                                draftKind: 'branch',
                                caseIndex: defaultCaseIndex,
                            }),
                        },
                    },
                },
                error: null,
            };

            const autoSelectedDefaultWhenState = reduceAction(seededState, {
                command: 'create_tool_step',
                toolHandle: CONSTANTS.Handles.Tools.TOOL_BooleanIdentity,
            }, generateIdentifier);

            if (autoSelectedDefaultWhenState.selectionState.selectedToolStep === null) {
                return autoSelectedDefaultWhenState;
            }

            const autoBoundDefaultWhenState = reduceAction(autoSelectedDefaultWhenState, {
                command: 'bind_materialized_resource',
                materializedResource: createBooleanMaterializedResource(true, generateIdentifier),
            }, generateIdentifier);

            if (autoBoundDefaultWhenState.phase !== 'reviewing_input_bindings') {
                return autoBoundDefaultWhenState;
            }

            return reduceAction(autoBoundDefaultWhenState, {
                command: 'finalize_current_tool_bindings',
            }, generateIdentifier);
        }

        case 'finalize_draft': {
            if (state.draft === null) {
                return withError(state, action.command, 'No active draft to finalize.');
            }

            if (isLoopDraft(state.draft)) {
                const whatToolStep = state.draft.caseDraft.what;
                const loopFeedbackBindingSpec = state.draft.caseDraft.loopFeedbackBindingSpec;
                const whatTool = whatToolStep ? state.toolMap.get(whatToolStep.toolHandle) : undefined;
                if (
                    !whatTool
                    || !loopFeedbackBindingSpec
                    || !isLoopFeedbackBindingSpecCompatible(whatTool, loopFeedbackBindingSpec)
                ) {
                    return withError(state, action.command, 'Loop draft has no valid feedback binding.');
                }
            }

            if (state.draft.draftKind === 'branch' && state.phase === 'reviewing_branch_cases') {
                return withError(state, action.command, 'Use Begin Default Case to enter the default branch case flow.');
            }

            const nextMutableWorkflow = mergeDraftIntoMutableWorkflow(state.draft, state.mutableWorkflow, generateIdentifier);

            return {
                ...withClearedSelection(state),
                phase: 'idle',
                draft: null,
                activeSlotCursor: null,
                mutableWorkflow: nextMutableWorkflow,
                error: null,
            };
        }

        case 'discard_draft': {
            if (!hasDraftAndActiveSlotCursor(state)) {
                return withError(state, action.command, 'No active draft to discard.');
            }

            const transition = deriveDiscardDraftTransition(state.activeSlotCursor);

            switch (transition.kind) {
                case 'to_idle':
                    return {
                        ...withClearedSelection(state),
                        phase: 'idle',
                        draft: null,
                        activeSlotCursor: null,
                        error: null,
                    };
                case 'loop_case_what': {
                    if (!isLoopDraft(state.draft)) {
                        return withError(state, action.command, 'Invalid loop draft scope.');
                    }
                    if (isLoopCaseCleared(state.draft)) {
                        return {
                            ...withClearedSelection(state),
                            phase: 'idle',
                            draft: null,
                            activeSlotCursor: null,
                            error: null,
                        };
                    }
                    return {
                        ...withClearedSelection(state),
                        phase: 'selecting_tool_at_slot',
                        draft: clearLoopCaseWhatAndWhen(state.draft),
                        activeSlotCursor: { cursorKind: 'case/what' },
                        error: null,
                    };
                }
                case 'loop_case_when': {
                    if (!isLoopDraft(state.draft)) {
                        return withError(state, action.command, 'Invalid loop draft scope.');
                    }
                    return {
                        ...withClearedSelection(state),
                        phase: 'binding_inputs',
                        draft: clearLoopCaseWhen(state.draft),
                        activeSlotCursor: { cursorKind: 'case/what' },
                        error: null,
                    };
                }
                case 'branch_case_when': {
                    if (!isBranchDraft(state.draft)) {
                        return withError(state, action.command, 'Invalid branch draft scope.');
                    }
                    const nextDraft = clearBranchCaseWhen(state.draft, transition.caseIndex);
                    if (!nextDraft) {
                        return withError(state, action.command, 'Target branch case is unavailable.');
                    }
                    return {
                        ...withClearedSelection(state),
                        phase: 'selecting_tool_at_slot',
                        draft: nextDraft,
                        activeSlotCursor: {
                            cursorKind: 'cases/when',
                            caseIndex: transition.caseIndex,
                        },
                        error: null,
                    };
                }
                case 'branch_case_what': {
                    if (!isBranchDraft(state.draft)) {
                        return withError(state, action.command, 'Invalid branch draft scope.');
                    }
                    const nextDraft = clearBranchCaseWhat(state.draft, transition.caseIndex);
                    if (!nextDraft) {
                        return withError(state, action.command, 'Target branch case is unavailable.');
                    }
                    return {
                        ...withClearedSelection(state),
                        phase: 'binding_inputs',
                        draft: nextDraft,
                        activeSlotCursor: {
                            cursorKind: 'cases/when',
                            caseIndex: transition.caseIndex,
                        },
                        error: null,
                    };
                }
                case 'branch_default_when': {
                    if (!isBranchDraft(state.draft)) {
                        return withError(state, action.command, 'Invalid branch default scope.');
                    }
                    return {
                        ...withClearedSelection(state),
                        phase: 'reviewing_branch_cases',
                        draft: clearBranchDefaultWhen(state.draft),
                        activeSlotCursor: { cursorKind: 'branch/editing' },
                        error: null,
                    };
                }
                case 'branch_default_what': {
                    if (!isBranchDraft(state.draft)) {
                        return withError(state, action.command, 'Invalid branch default scope.');
                    }
                    const nextDraft = clearBranchDefaultWhat(state.draft);
                    if (!nextDraft) {
                        return withError(state, action.command, 'Invalid branch default scope.');
                    }
                    return {
                        ...withClearedSelection(state),
                        phase: 'reviewing_branch_cases',
                        draft: nextDraft,
                        activeSlotCursor: { cursorKind: 'branch/editing' },
                        error: null,
                    };
                }
            }
        }

        case 'commit_workflow': {
    if (state.draft !== null) {
        return withError(state, action.command, 'Please finalize or discard the current draft before committing workflow.');
    }

    return {
        ...withClearedSelection(resetExecution(state)),
        phase: 'workflow_ready',
        draft: null,
        activeSlotCursor: null,
        error: null,
    };
}

        case 'run_workflow': {
            if (state.phase !== 'workflow_ready' || state.draft !== null) {
                return withError(state, action.command, 'Workflow must be ready before running.');
            }
            return {
                ...state,
                error: null,
            };
        }

        case 'dispatch_workflow': {
            return withError(state, action.command, 'Dispatch is not wired yet.');
        }

        case 'commit_workflow_result': {
            if (action.ok) {
                return {
                    ...state,
                    phase: 'workflow_ready',
                    error: null,
                };
            }

            return withError(
                {
                    ...state,
                    phase: 'idle',
                },
                action.command,
                action.message,
            );
        }

        case 'discard_workflow': {
            if (state.draft !== null) {
                return withError(state, action.command, 'Please discard the draft before discarding workflow.');
            }

            return {
                ...withClearedSelection(resetExecution(state)),
                phase: 'idle',
                draft: null,
                activeSlotCursor: null,
                mutableWorkflow: {
                    ...state.mutableWorkflow,
                    stepsByThreadIndex: [[]],
                    workflowState: {},
                },
                error: null,
            };
        }

        case 'invalidate_workflow_ready': {
            if (state.phase !== 'workflow_ready') {
                return state;
            }
            return {
                ...state,
                phase: 'idle',
                error: null,
            };
        }

        default: {
            const _exhaustive: never = action;
            return _exhaustive;
        }
    }
}
